import { useRef, useCallback, useState, useEffect } from "react";
import { UseFormReturn, FieldValues } from "react-hook-form";

type FormInstance = UseFormReturn<FieldValues>;
type FormSaveHandler = () => Promise<void> | void;
type FormEntry = {
  form: FormInstance;
  save?: FormSaveHandler;
};

/**
 * Hook to track dirty state across multiple react-hook-form instances.
 *
 * This is used in multi-section forms (like PVP applications) where each section
 * has its own form instance, but we need to know if ANY form has unsaved changes
 * for navigation guards and submit button state.
 *
 * IMPLEMENTATION NOTES:
 * - react-hook-form doesn't natively support tracking state across multiple forms
 * - We use a ref to store form instances and subscribe to their changes via `watch()`
 * - A debounced state update prevents infinite re-render loops that occur when
 *   state updates trigger re-renders which trigger more watch callbacks
 * - The 50ms debounce batches rapid changes (e.g., typing) into single updates
 *
 * ALTERNATIVE APPROACHES CONSIDERED:
 * - Non-reactive: Only check dirty state on user actions (simpler but no real-time UI feedback)
 * - useSyncExternalStore: More "React-correct" but adds complexity for same result
 * - Polling: Previous implementation used setInterval which was unreliable and wasteful
 */
export const useFormInstances = () => {
  const formInstancesRef = useRef<Map<FormInstance, FormEntry>>(new Map());
  const [isDirty, setIsDirty] = useState(false);
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Debounced update to prevent infinite loops.
   * When a form changes, we wait 50ms before updating state to batch rapid changes.
   */
  const scheduleUpdate = useCallback(() => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    updateTimeoutRef.current = setTimeout(() => {
      const dirty = Array.from(formInstancesRef.current.keys()).some((form) => form.formState.isDirty);
      setIsDirty(dirty);
    }, 50);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Register a form instance to track its dirty state.
   * Returns an unregister function to call on form unmount.
   */
  const registerForm = useCallback(
    (form: FormInstance, save?: FormSaveHandler) => {
      formInstancesRef.current.set(form, { form, save });
      scheduleUpdate();

      // Subscribe to form changes to trigger re-renders in consuming components
      const subscription = form.watch(() => {
        scheduleUpdate();
      });

      return () => {
        subscription.unsubscribe();
        formInstancesRef.current.delete(form);
        scheduleUpdate();
      };
    },
    [scheduleUpdate],
  );

  /**
   * Check if any registered form has unsaved changes.
   * Uses both reactive state (for triggering re-renders) and direct check (for accuracy).
   */
  const hasDirtyForms = useCallback(() => {
    // Check both reactive state AND current form state for accuracy
    // The reactive state triggers re-renders, the direct check ensures accuracy
    return isDirty || Array.from(formInstancesRef.current.keys()).some((form) => form.formState.isDirty);
  }, [isDirty]);

  const saveDirtyForms = useCallback(async () => {
    const entries = Array.from(formInstancesRef.current.values());
    for (const entry of entries) {
      if (!entry.form.formState.isDirty || !entry.save) {
        continue;
      }
      try {
        await entry.save();
      } catch (error) {
        console.error("Failed to save a dirty form:", error);
      }
    }
  }, []);

  const saveAllForms = useCallback(async () => {
    const entries = Array.from(formInstancesRef.current.values());
    for (const entry of entries) {
      if (!entry.save) {
        continue;
      }
      try {
        await entry.save();
      } catch (error) {
        console.error("Failed to save a form:", error);
      }
    }
  }, []);

  return {
    registerForm,
    hasDirtyForms,
    saveDirtyForms,
    saveAllForms,
  };
};
